import * as React from 'react';
import { cn } from '@packages/utils';

/* ------------------------------------------------------------------
   CMS content is stored as TipTap / ProseMirror JSON (StarterKit +
   Image + Link + TextAlign + TextStyle/Color). We render that JSON into
   collage-styled prose. A plain HTML string is also accepted as a
   fallback (rendered verbatim).
   ------------------------------------------------------------------ */

type Mark = { type: string; attrs?: Record<string, unknown> };
type Node = {
  type: string;
  attrs?: Record<string, unknown>;
  marks?: Mark[];
  text?: string;
  content?: Node[];
};

const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

const alignStyle = (attrs?: Record<string, unknown>): React.CSSProperties | undefined => {
  const a = attrs?.textAlign;
  return typeof a === 'string' && a !== 'left'
    ? { textAlign: a as React.CSSProperties['textAlign'] }
    : undefined;
};

const isWwButton = (attrs?: Record<string, unknown>) => {
  const value = attrs?.wwButton;
  return value === true || value === 'true' || value === 'button' || value === 'primary';
};

const getSafeHref = (href: string) => {
  const trimmed = href.trim();
  if (!trimmed) return '#';

  try {
    const protocol = new URL(trimmed, 'https://wuest-und-wild.local').protocol;
    if (!SAFE_LINK_PROTOCOLS.includes(protocol.toLowerCase())) return '#';
  } catch {
    return '#';
  }

  return trimmed;
};

const renderText = (node: Node, key: number): React.ReactNode => {
  let el: React.ReactNode = node.text ?? '';
  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
        el = <strong>{el}</strong>;
        break;
      case 'italic':
        el = <em>{el}</em>;
        break;
      case 'strike':
        el = <s>{el}</s>;
        break;
      case 'code':
        el = <code>{el}</code>;
        break;
      case 'link': {
        const href = getSafeHref(typeof mark.attrs?.href === 'string' ? mark.attrs.href : '#');
        const buttonLink = isWwButton(mark.attrs);
        el = (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={buttonLink ? 'ww-btn ww-btn--ink' : undefined}
            data-ww-button={buttonLink ? 'true' : undefined}>
            {el}
          </a>
        );
        break;
      }
      case 'textStyle': {
        const color = mark.attrs?.color;
        if (typeof color === 'string') el = <span style={{ color }}>{el}</span>;
        break;
      }
    }
  }
  return <React.Fragment key={key}>{el}</React.Fragment>;
};

const renderNodes = (nodes?: Node[]): React.ReactNode =>
  (nodes ?? []).map((child, i) => renderNode(child, i));

function renderNode(node: Node, key: number): React.ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, key);
    case 'paragraph':
      return (
        <p key={key} style={alignStyle(node.attrs)}>
          {renderNodes(node.content)}
        </p>
      );
    case 'heading': {
      const level = Math.min(Math.max(Number(node.attrs?.level ?? 2), 1), 4);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      return (
        <Tag key={key} style={alignStyle(node.attrs)}>
          {renderNodes(node.content)}
        </Tag>
      );
    }
    case 'bulletList':
      return <ul key={key}>{renderNodes(node.content)}</ul>;
    case 'orderedList':
      return <ol key={key}>{renderNodes(node.content)}</ol>;
    case 'listItem':
      return <li key={key}>{renderNodes(node.content)}</li>;
    case 'blockquote':
      return <blockquote key={key}>{renderNodes(node.content)}</blockquote>;
    case 'codeBlock':
      return (
        <pre key={key}>
          <code>{renderNodes(node.content)}</code>
        </pre>
      );
    case 'horizontalRule':
      return <hr key={key} />;
    case 'hardBreak':
      return <br key={key} />;
    case 'image': {
      const src = typeof node.attrs?.src === 'string' ? node.attrs.src : '';
      const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
      return src ? <img key={key} src={src} alt={alt} /> : null;
    }
    default:
      return node.content ? (
        <React.Fragment key={key}>{renderNodes(node.content)}</React.Fragment>
      ) : null;
  }
}

const tryParseDoc = (value: string): Node | null => {
  try {
    const parsed = JSON.parse(value) as Node;
    return parsed && typeof parsed === 'object' && parsed.type === 'doc' ? parsed : null;
  } catch {
    return null;
  }
};

/** Renders CMS rich text (TipTap JSON, or an HTML string fallback) with the
 * collage prose styling. `variant="event"` inherits `--ev-*` colors. */
export function WwProse({
  html,
  variant = 'festival',
  className,
}: {
  html: string;
  variant?: 'festival' | 'event';
  className?: string;
}) {
  const cls = cn(variant === 'event' ? 'ev-prose' : 'ww-prose', className);
  if (!html) return null;

  const doc = tryParseDoc(html);
  if (doc) {
    return <div className={cls}>{renderNodes(doc.content)}</div>;
  }
  return <div className={cls} dangerouslySetInnerHTML={{ __html: html }} />;
}
